import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Flame, Droplets, Sparkles, Loader2, Swords, Lock, RotateCcw, Send, Users, Share2, ArrowRight } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { bumpToolStat } from "@/lib/toolStats";
import { CinematicVideo } from "@/components/CinematicVideo";
import { AiThinking } from "@/components/AiThinking";
import { DialecticCore } from "@/components/DialecticCore";
import { ShareBar } from "@/components/ShareBar";

const DEUX_VOIES_VIDEO = "https://customer-assets-7cd3h4nn.emergentagent.net/job_epiphany-mentor/artifacts/e2tpyd31_gemini_generated_video_3f9379d1.mp4";

const VOTE_T = {
  fr: { title: "Le Verdict de la Foule", prompt: "Et toi, où penches-tu ?", share: "Partager le verdict", copied: "Verdict copié !", votes: "votes", vote: "vote", debateLabel: "Le débat",
        text: (q, a, n) => `« ${q} » — Le Verdict de la Foule sur COCOON MIND : L'Ardent ${a}% · Le Nuancé ${n}%. Et toi ? cocoonmind.com` },
  en: { title: "The Crowd's Verdict", prompt: "And you, which way do you lean?", share: "Share the verdict", copied: "Verdict copied!", votes: "votes", vote: "vote", debateLabel: "The debate",
        text: (q, a, n) => `"${q}" — The Crowd's Verdict on COCOON MIND: The Ardent ${a}% · The Nuanced ${n}%. And you? cocoonmind.com` },
  es: { title: "El Veredicto de la Multitud", prompt: "Y tú, ¿hacia dónde te inclinas?", share: "Compartir el veredicto", copied: "¡Veredicto copiado!", votes: "votos", vote: "voto", debateLabel: "El debate",
        text: (q, a, n) => `«${q}» — El Veredicto de la Multitud en COCOON MIND: El Ardiente ${a}% · El Matizado ${n}%. ¿Y tú? cocoonmind.com` },
  nl: { title: "Het Oordeel van de Menigte", prompt: "En jij, naar welke kant neig je?", share: "Deel het oordeel", copied: "Oordeel gekopieerd!", votes: "stemmen", vote: "stem", debateLabel: "Het debat",
        text: (q, a, n) => `'${q}' — Het Oordeel van de Menigte op COCOON MIND: De Vurige ${a}% · De Genuanceerde ${n}%. En jij? cocoonmind.com` },
};

export default function DeuxVoies({ onExplore }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [topic, setTopic] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [revealed, setRevealed] = useState(0);
  const [locked, setLocked] = useState(false);
  const timerRef = useRef(null);
  const bottomRef = useRef(null);

  const dv = t("atelier.dv", { returnObjects: true }) || {};
  const presets = Array.isArray(dv.presets) ? dv.presets : [];
  const lng = (i18n.language || "fr").slice(0, 2);
  const vt = VOTE_T[lng] || VOTE_T.fr;
  const [tally, setTally] = useState(null);
  const [voting, setVoting] = useState(false);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [revealed, data, loading]);

  const run = async (q) => {
    const question = ((q ?? topic) || "").trim();
    if (!question || loading) return;
    clearTimeout(timerRef.current);
    setLoading(true);
    setData(null);
    setRevealed(0);
    setLocked(false);
    setTopic(question);
    try {
      const { data: res } = await api.post("/deux-voies", {
        topic: question,
        lang: (i18n.language || "fr").slice(0, 2),
      });
      setData(res);
      bumpToolStat("deuxVoies");
      let i = 0;
      const step = () => {
        i += 1;
        setRevealed(i);
        if (i < (res.exchanges?.length || 0)) timerRef.current = setTimeout(step, 1600);
      };
      timerRef.current = setTimeout(step, 500);
    } catch (e) {
      if (e.response?.status === 402) setLocked(true);
      else toast.error(formatApiErrorDetail(e.response?.data?.detail) || dv.error);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    clearTimeout(timerRef.current);
    setData(null);
    setRevealed(0);
    setTopic("");
    setTally(null);
  };

  const deepen = () => {
    if (!data) return;
    const prompt = t("atelier.dv.deepenPrompt", { topic: data.topic });
    if (onExplore) onExplore(prompt);
  };

  const allShown = data && revealed >= (data.exchanges?.length || 0);
  const lastSpeaker = data && revealed > 0 ? data.exchanges?.[revealed - 1]?.speaker : null;
  const coreLean = tally?.my_choice ? (tally.ardent_pct - tally.nuance_pct) / 100 : lastSpeaker === "ardent" ? 0.6 : lastSpeaker === "nuance" ? -0.6 : 0;

  useEffect(() => {
    if (allShown && data?.topic) {
      api.get("/deux-voies/tally", { params: { topic: data.topic, lang: lng } })
        .then(({ data: d }) => setTally(d)).catch(() => {});
    }
  }, [allShown, data, lng]);

  const vote = async (choice) => {
    if (!data?.topic || voting) return;
    setVoting(true);
    try {
      const { data: d } = await api.post("/deux-voies/vote", { topic: data.topic, choice, lang: lng });
      setTally(d);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || dv.error);
    } finally {
      setVoting(false);
    }
  };

  const shareVerdict = async () => {
    const a = tally?.ardent_pct ?? 50, n = tally?.nuance_pct ?? 50;
    const text = vt.text(data.topic, a, n);
    try {
      if (navigator.share) await navigator.share({ text });
      else { await navigator.clipboard.writeText(text); toast.success(vt.copied); }
    } catch { /* dismissed */ }
  };

  return (
    <div className="h-full overflow-y-auto scroll-thin">
      <div className="max-w-3xl mx-auto px-5 sm:px-6 py-10 sm:py-14">
        {/* header */}
        <div className="text-center mb-8">
          <span className="inline-flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.3em] text-primary/70 mb-3">
            <Swords size={14} /> {dv.eyebrow}
          </span>
          <h1 className="font-display text-3xl sm:text-4xl text-gradient-orange leading-tight mb-3" data-testid="dv-title">
            {dv.title}
          </h1>
          <p className="text-foreground/60 leading-relaxed max-w-xl mx-auto text-sm sm:text-base">{dv.subtitle}</p>
        </div>

        {/* cinématique */}
        <div className="mb-6">
          <CinematicVideo src={DEUX_VOIES_VIDEO} testId="deux-voies-cinematic-video" fade sound />
        </div>


        {/* the two personas */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <div className="rounded-2xl border border-primary/25 bg-primary/[0.06] px-4 py-3 flex items-center gap-3">
            <span className="w-9 h-9 rounded-xl bg-primary/15 flex items-center justify-center text-primary flex-shrink-0">
              <Flame size={17} />
            </span>
            <span className="font-display text-primary text-sm sm:text-base">{dv.ardent}</span>
          </div>
          <div className="rounded-2xl border border-sky-300/20 bg-sky-300/[0.05] px-4 py-3 flex items-center gap-3">
            <span className="w-9 h-9 rounded-xl bg-sky-300/12 flex items-center justify-center text-sky-300/90 flex-shrink-0">
              <Droplets size={17} />
            </span>
            <span className="font-display text-foreground/85 text-sm sm:text-base">{dv.nuance}</span>
          </div>
        </div>

        <DialecticCore variant="tug" lean={data ? coreLean : undefined} testId="dv-core" />

        {/* input */}
        <form
          onSubmit={(e) => { e.preventDefault(); run(); }}
          className="rounded-2xl bg-white/[0.03] border border-white/12 p-3 flex items-center gap-2 mb-4"
          data-testid="dv-form"
        >
          <input
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder={dv.placeholder}
            data-testid="dv-input"
            className="flex-1 bg-transparent px-3 py-2 text-sm sm:text-base text-foreground placeholder:text-foreground/35 focus:outline-none"
          />
          <button
            type="submit"
            disabled={loading || !topic.trim()}
            data-testid="dv-submit"
            className="inline-flex items-center gap-2 rounded-full bg-primary/15 border border-primary/40 text-primary px-5 py-2.5 text-sm font-medium hover:bg-primary/25 transition-colors disabled:opacity-50 flex-shrink-0"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
            <span className="hidden sm:inline">{dv.go}</span>
          </button>
        </form>

        {/* presets */}
        {!data && !loading && (
          <div className="mb-6" data-testid="dv-presets">
            <p className="text-xs text-foreground/40 mb-2.5">{dv.suggest}</p>
            <div className="flex flex-wrap gap-2">
              {presets.map((p, i) => (
                <button
                  key={i}
                  onClick={() => run(p)}
                  data-testid={`dv-preset-${i}`}
                  className="text-xs sm:text-sm px-3.5 py-2 rounded-full border border-primary/20 text-foreground/70 hover:text-primary hover:bg-primary/[0.06] hover:border-primary/40 transition-colors"
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* locked */}
        {locked && (
          <motion.div
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            className="glass rounded-2xl border border-primary/25 p-8 text-center frame-pulse"
            data-testid="dv-locked"
          >
            <span className="w-14 h-14 mx-auto rounded-2xl bg-primary/10 flex items-center justify-center text-primary mb-4">
              <Lock size={24} strokeWidth={1.6} />
            </span>
            <p className="font-display text-xl text-foreground/90 mb-2">{dv.locked}</p>
            <p className="text-sm text-foreground/55 leading-relaxed max-w-md mx-auto mb-6">{dv.lockedDesc}</p>
            <button
              onClick={() => navigate("/tarifs")}
              data-testid="dv-locked-cta"
              className="btn-frost-orange inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm"
            >
              <Sparkles size={15} /> {dv.lockedCta}
            </button>
          </motion.div>
        )}

        {/* loading state (before first bubble) */}
        {loading && !data && <AiThinking variant="debate" testId="dv-loading" />}

        {/* debate */}
        {data && (
          <div className="space-y-4" data-testid="dv-debate">
            <AnimatePresence>
              {data.exchanges.slice(0, revealed).map((ex, i) => {
                const ardent = ex.speaker === "ardent";
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 18, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.45 }}
                    className={`flex ${ardent ? "justify-start" : "justify-end"}`}
                    data-testid={`dv-bubble-${i}`}
                  >
                    <div className={`max-w-[85%] rounded-2xl px-4 py-3 border ${
                      ardent
                        ? "bg-primary/[0.07] border-primary/25 rounded-tl-sm"
                        : "bg-sky-300/[0.05] border-sky-300/20 rounded-tr-sm"
                    }`}>
                      <p className={`font-display text-xs mb-1.5 flex items-center gap-1.5 ${ardent ? "text-primary" : "text-sky-300/90"}`}>
                        {ardent ? <Flame size={13} /> : <Droplets size={13} />}
                        {ardent ? dv.ardent : dv.nuance}
                      </p>
                      <p className="text-sm sm:text-[0.95rem] text-foreground/85 leading-relaxed">{ex.text}</p>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>

            {/* typing indicator between reveals */}
            {revealed < data.exchanges.length && (
              <div className="flex items-center gap-2 text-foreground/40 text-xs italic pl-1" data-testid="dv-typing">
                <Loader2 size={13} className="animate-spin" /> {dv.thinking}
              </div>
            )}

            {/* synthesis + actions */}
            {allShown && (
              <motion.div
                initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="pt-2"
              >
                {data.synthesis && (
                  <div className="glass rounded-2xl border border-primary/20 p-5 text-center stats-wow" data-testid="dv-synthesis">
                    <p className="uppercase tracking-[0.25em] text-[0.7rem] text-primary/70 mb-2 flex items-center justify-center gap-1.5">
                      <Sparkles size={13} /> {dv.synthesis}
                    </p>
                    <p className="font-display italic text-base sm:text-lg text-foreground/85 leading-relaxed">{data.synthesis}</p>
                  </div>
                )}

                <div className="glass rounded-2xl border border-primary/20 p-5 mt-4" data-testid="dv-verdict">
                  <p className="uppercase tracking-[0.25em] text-[0.7rem] text-primary/70 mb-3 flex items-center justify-center gap-1.5"><Users size={13} /> {vt.title}</p>
                  {!tally?.my_choice ? (
                    <>
                      <p className="text-center text-sm text-foreground/60 mb-3">{vt.prompt}</p>
                      <div className="grid grid-cols-2 gap-3">
                        <button onClick={() => vote("ardent")} disabled={voting} data-testid="dv-vote-ardent"
                          className="rounded-xl border border-primary/30 bg-primary/[0.06] px-4 py-3 text-primary hover:bg-primary/12 transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-50">
                          <Flame size={16} /> {dv.ardent}
                        </button>
                        <button onClick={() => vote("nuance")} disabled={voting} data-testid="dv-vote-nuance"
                          className="rounded-xl border border-sky-300/25 bg-sky-300/[0.05] px-4 py-3 text-sky-300/90 hover:bg-sky-300/12 transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-50">
                          <Droplets size={16} /> {dv.nuance}
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="space-y-2.5" data-testid="dv-verdict-bars">
                      {[["ardent", dv.ardent, tally.ardent_pct, "bg-primary"], ["nuance", dv.nuance, tally.nuance_pct, "bg-sky-300"]].map(([k, label, pct, bar]) => (
                        <div key={k}>
                          <div className="flex justify-between text-xs mb-1">
                            <span className={k === tally.my_choice ? "text-primary font-semibold" : "text-foreground/60"}>{label}</span>
                            <span className="text-foreground/60">{pct}%</span>
                          </div>
                          <div className="h-2.5 rounded-full bg-white/[0.08] overflow-hidden">
                            <div className={`h-full ${bar}`} style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      ))}
                      <p className="text-center text-xs text-foreground/40 pt-1">{tally.total} {tally.total > 1 ? vt.votes : vt.vote}</p>
                      <div className="text-center pt-1">
                        <button onClick={shareVerdict} data-testid="dv-verdict-share"
                          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm text-primary/90 border border-primary/30 hover:bg-primary/10 transition-colors">
                          <Share2 size={15} /> {vt.share}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <ShareBar
                  lang={lng}
                  endpoint="/dialectic/report-card"
                  payload={{
                    mode: "duel",
                    eyebrow: dv.eyebrow,
                    question: data.topic,
                    left_label: dv.ardent,
                    right_label: dv.nuance,
                    left_value: tally?.my_choice ? `${tally.ardent_pct}%` : "",
                    right_value: tally?.my_choice ? `${tally.nuance_pct}%` : "",
                    exchanges: data.exchanges || [],
                    lang: lng,
                  }}
                  summary={tally?.my_choice
                    ? vt.text(data.topic, tally.ardent_pct, tally.nuance_pct)
                    : `« ${data.topic} » — ${dv.eyebrow} · COCOON MIND. ${vt.prompt} cocoonmind.com`}
                  title={dv.eyebrow}
                  subject={dv.title}
                  filename="cocoon-deux-voies.png"
                  testPrefix="dv-share"
                  className="mt-4"
                />

                <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-5">
                  <button
                    onClick={deepen}
                    data-testid="dv-deepen"
                    className="btn-frost-orange inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm"
                  >
                    <Sparkles size={15} /> {dv.deepen}
                  </button>
                  <button
                    onClick={reset}
                    data-testid="dv-again"
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm text-foreground/60 hover:text-primary border border-white/10 hover:border-primary/30 transition-colors"
                  >
                    <RotateCcw size={15} /> {dv.again}
                  </button>
                </div>
              </motion.div>
            )}
            <div ref={bottomRef} />
          </div>
        )}
      </div>
    </div>
  );
}
